import { useState, useEffect, useCallback, useRef } from 'react'
import { CompanyService } from '../../../services/CompanyService'
import { trackEvent } from '../../../services/analyticsService'
import { Company, CompanyUser, CompanyUserRole, BillingTier } from '../../../core/models/Company'
import { addNotification } from '../../../stores/NotificationStore'
import store from '../../../stores'
import {
  CompanyWithStats,
  CompanyFormData,
  InviteUserFormData,
  EditUserFormData,
  DialogState,
  LoadingState,
  UserMenuState,
  SelectedItems,
  AVAILABLE_PERMISSIONS,
} from '../types'
import {
  companyDefaultRoleValue,
  memberRoleValue,
  withoutUnchangedDefaultRole,
} from '../utils/roleOptions'
import { userEditChanges } from '../utils/userEditChanges'
import { billingFormValues, changedBillingKeys, withoutBillingKeys } from '../utils/billingKeys'
import { useElevatedAdmin, useSurface } from '../../../hooks/usePermission'

export const useCompanyManagement = (
  isPlatformAdmin: boolean,
  isCompanyManagerOrAdmin: boolean,
  isGlobalAdmin: boolean,
  currentCompany: Company | null
) => {
  // Server-verified, unlike the `isPlatformAdmin` argument: it decides what a save may carry.
  const verifiedPlatformAdmin = useElevatedAdmin()
  // The Edit User role picker's key: cloud refuses a save that carries a role without it.
  const canAssignRole = useSurface('edit-user-dialog.role-picker')

  // User state
  const [userRole, setUserRole] = useState<string>('')
  const [userPermissions, setUserPermissions] = useState<string[]>([])
  const [isCompanyManager, setIsCompanyManager] = useState(false)

  // Data state
  const [companies, setCompanies] = useState<CompanyWithStats[]>([])
  const [companyUsers, setCompanyUsers] = useState<CompanyUser[]>([])
  // Authoritative total from the server count() query — accurate even when pagination is in use
  const [totalCompanyUsers, setTotalCompanyUsers] = useState<number | null>(null)

  // Loading state
  const [loading, setLoading] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [loadingUserInfo, setLoadingUserInfo] = useState(true)
  // Ref-based guard to prevent parallel duplicate loadCompanyUsers calls
  const loadingUsersRef = useRef(false)
  // The default role the company form was filled with, to tell whether the person changed it.
  const defaultRoleAtOpenRef = useRef<string | null>(null)

  // Dialog state
  const [dialogs, setDialogs] = useState<DialogState>({
    showCreateDialog: false,
    showEditCompanyDialog: false,
    showUsersDialog: false,
    showInviteDialog: false,
    showEditUserDialog: false,
  })

  // Form state
  const [companyForm, setCompanyForm] = useState<CompanyFormData>({
    name: '',
    domain: '',
    subdomain: '',
    matrix_chat_domain: '',
    platform_name: '',
    platform_url: '',
    logo_url: '',
    primary_color: '#389acb',
    secondary_color: '#ffffff',
    accent_color: '',
    link_color: '',
    surface_tint_color: '',
    allowed_domains: [''],
    auto_assignment_enabled: true,
    default_role: CompanyUserRole.USER,
    max_users: 100,
    max_rooms: 50,
    billing_tier: BillingTier.STARTER,
    base_price: 0,
    price_per_user: 0,
    features: {
      mapEditor: true,
      chat: true,
      whiteboard: true,
      screenSharing: true,
      videoCalling: true,
      customBranding: true,
      advancedAnalytics: false,
      apiAccess: false,
    },
    matrix_chat_enabled: false,
  })

  const [inviteForm, setInviteForm] = useState<InviteUserFormData>({
    email: '',
    role: CompanyUserRole.USER,
    department: '',
    title: '',
    permissions: [],
    selectedUser: null,
    invitationType: 'email',
  })

  const [editUserForm, setEditUserForm] = useState<EditUserFormData>({
    role: CompanyUserRole.USER,
    department: '',
    title: '',
    permissions: [],
  })

  // Selected items
  const [selectedItems, setSelectedItems] = useState<SelectedItems>({
    selectedCompany: null,
    selectedUser: null,
  })

  // User menu state
  const [userMenu, setUserMenu] = useState<UserMenuState>({
    userMenuAnchor: null,
    selectedUserForMenu: null,
  })

  // Load user information and determine role
  useEffect(() => {
    // If we already have company data or are platform/global admin, skip loading
    if (currentCompany || isPlatformAdmin || isGlobalAdmin) {
      setLoadingUserInfo(false)
      return
    }

    // Prevent multiple simultaneous loads
    let isMounted = true

    const loadUserInfo = async () => {
      try {
        if (!isMounted) return
        setLoadingUserInfo(true)
        
        const companyResult = await CompanyService.getCurrentUserCompany()
        
        if (!isMounted) return
        
        if (companyResult.success && companyResult.company) {
          setUserRole(companyResult.user_role || 'user')
          setUserPermissions(companyResult.permissions || [])
          
          // Check if user is company manager
          const managerResult = await CompanyService.isCompanyManager(companyResult.company.id)
          if (isMounted) {
            setIsCompanyManager(managerResult)
          }
        }
      } catch (error) {
        console.error('Error loading user info:', error)
      } finally {
        if (isMounted) {
          setLoadingUserInfo(false)
        }
      }
    }

    // Add a timeout to prevent infinite loading
    const timeoutId = setTimeout(() => {
      if (isMounted) {
        setLoadingUserInfo(false)
      }
    }, 5000) // 5 second timeout

    loadUserInfo()

    return () => {
      isMounted = false
      clearTimeout(timeoutId)
    }
  }, [currentCompany?.id, isPlatformAdmin, isGlobalAdmin]) // Use currentCompany.id instead of currentCompany object

  // Load companies (only for platform admins)
  useEffect(() => {
    if (isPlatformAdmin) {
      loadCompanies()
    }
  }, [isPlatformAdmin])

  // Note: company users are intentionally NOT auto-loaded here.
  // Each consumer (CompanyManagerView, PlatformAdminView) controls when to load
  // users via loadCompanyUsers() to prevent duplicate in-flight requests.

  const loadCompanies = useCallback(async () => {
    try {
      setLoading(true)
      const result = await CompanyService.getAllCompanies()
      
      if (result.success && result.companies) {
        setCompanies(result.companies)
      } else {
        setCompanies([])
        if (result.error) {
          store.dispatch(
            addNotification({
              message: result.error,
              type: 'error',
              duration: 5000,
            })
          )
        }
      }
    } catch (error) {
      console.error('Error loading companies:', error)
      store.dispatch(
        addNotification({
          message: 'Failed to load companies',
          type: 'error',
          duration: 5000,
        })
      )
    } finally {
      setLoading(false)
    }
  }, [])

  const loadCompanyUsers = useCallback(async (companyId: string) => {
    // Prevent parallel duplicate requests for the same company
    if (loadingUsersRef.current) return
    loadingUsersRef.current = true
    try {
      setLoadingUsers(true)
      const result = await CompanyService.getCompanyUsersAll(companyId)
      
      if (result.success && result.users) {
        setCompanyUsers(result.users)
        if (result.total !== undefined) {
          setTotalCompanyUsers(result.total)
        }
      } else {
        setCompanyUsers([])
        setTotalCompanyUsers(null)
        if (result.error) {
          store.dispatch(
            addNotification({
              message: result.error,
              type: 'error',
              duration: 5000,
            })
          )
        }
      }
    } catch (error) {
      console.error('Error loading company users:', error)
      store.dispatch(
        addNotification({
          message: 'Failed to load company users',
          type: 'error',
          duration: 5000,
        })
      )
    } finally {
      setLoadingUsers(false)
      loadingUsersRef.current = false
    }
  }, [])

  const resetCompanyForm = useCallback(() => {
    setCompanyForm({
      name: '',
      domain: '',
      subdomain: '',
      platform_name: '',
      platform_url: '',
      logo_url: '',
      primary_color: '#389acb',
      secondary_color: '#ffffff',
      accent_color: '',
      link_color: '',
      surface_tint_color: '',
      allowed_domains: [''],
      auto_assignment_enabled: true,
      default_role: CompanyUserRole.USER,
      max_users: 100,
      max_rooms: 50,
      billing_tier: BillingTier.STARTER,
      base_price: 0,
      price_per_user: 0,
      features: {
        mapEditor: true,
        chat: true,
        whiteboard: true,
        screenSharing: true,
        videoCalling: true,
        customBranding: true,
        advancedAnalytics: false,
        apiAccess: false,
      },
      matrix_chat_enabled: false,
    })
  }, [])

  const handleCreateCompany = useCallback(async () => {
    try {
      // The form starts at Member; resending that would set the default role on a new company
      // for nobody's reason, so only a role the person picked is sent.
      const result = await CompanyService.createCompany(
        withoutUnchangedDefaultRole(companyForm, CompanyUserRole.USER)
      )

      if (result.success && result.company) {
        trackEvent(
          'APP_company_creation_success',
          {
            source: isPlatformAdmin ? 'platform_admin' : 'company_management_admin',
            company_id: result.company.id ?? null,
            company_name: result.company.name ?? null,
          },
          isPlatformAdmin ? 'Admin' : 'Company'
        )
        store.dispatch(
          addNotification({
            message: 'Company created successfully',
            type: 'success',
            duration: 5000,
          })
        )
        setDialogs(prev => ({ ...prev, showCreateDialog: false }))
        resetCompanyForm()
        loadCompanies()
      } else {
        store.dispatch(
          addNotification({
            message: result.error || 'Failed to create company',
            type: 'error',
            duration: 5000,
          })
        )
      }
    } catch (error) {
      console.error('Error creating company:', error)
      store.dispatch(
        addNotification({
          message: 'Error creating company',
          type: 'error',
          duration: 5000,
        })
      )
    }
  }, [companyForm, resetCompanyForm, loadCompanies, isPlatformAdmin])

  const handleInviteUser = useCallback(async () => {
    // The company the admin selected comes before the currentCompany prop: the prop is loaded once
    // and goes stale, and in the platform view it is not the company whose users are open.
    const targetCompany = selectedItems.selectedCompany || currentCompany
    
    if (!targetCompany) {
      store.dispatch(
        addNotification({
          message: 'Please select a company to invite users to',
          type: 'error',
          duration: 5000,
        })
      )
      return
    }

    try {
      // Prepare user data for billing-aware invitation
      const userData = {
        email: inviteForm.email,
        name: inviteForm.selectedUser?.displayName || inviteForm.email.split('@')[0],
        role: inviteForm.role,
        department: inviteForm.department,
        title: inviteForm.title,
        permissions: inviteForm.permissions,
        invitationType: inviteForm.invitationType,
        selectedUser: inviteForm.selectedUser
      }

      const result = await CompanyService.addUserWithBilling(
        targetCompany.id,
        userData
      )

      if (result.success) {
        store.dispatch(
          addNotification({
            // Cloud returns requires_payment false for an instant add (within the free limit) and for
            // a person already in the company; only the pending paid add needs payment.
            message: result.requires_payment
              ? 'User added to pending list successfully. Please complete payment to activate.'
              : 'User added successfully.',
            type: 'success',
            duration: 5000,
          })
        )
        setDialogs(prev => ({ ...prev, showInviteDialog: false }))
        setInviteForm({
          email: '',
          role: CompanyUserRole.USER,
          department: '',
          title: '',
          permissions: [],
          selectedUser: null,
          invitationType: 'email',
        })
        loadCompanyUsers(targetCompany.id)
        // Reload companies to update user counts
        if (isPlatformAdmin || isGlobalAdmin) {
          loadCompanies()
        }
      } else {
        store.dispatch(
          addNotification({
            message: result.error || 'Failed to add user to pending list',
            type: 'error',
            duration: 5000,
          })
        )
      }
    } catch (error) {
      console.error('Error adding user to pending list:', error)
      store.dispatch(
        addNotification({
          message: 'Error adding user to pending list',
          type: 'error',
          duration: 5000,
        })
      )
    }
  }, [currentCompany, selectedItems, inviteForm, loadCompanyUsers, loadCompanies, isPlatformAdmin, isGlobalAdmin])

  const handleUserMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, user: CompanyUser) => {
    console.log('handleUserMenuOpen called with:', { event: event.currentTarget, user })
    setUserMenu({
      userMenuAnchor: event.currentTarget,
      selectedUserForMenu: user,
    })
    console.log('userMenu state set to:', { userMenuAnchor: event.currentTarget, selectedUserForMenu: user })
  }, [])

  const handleUserMenuClose = useCallback(() => {
    console.log('handleUserMenuClose called')
    setUserMenu({
      userMenuAnchor: null,
      selectedUserForMenu: null,
    })
  }, [])

  const handleRemoveUser = useCallback(async () => {
    // Selected company before the stale currentCompany prop, as in handleInviteUser.
    const targetCompany = selectedItems.selectedCompany || currentCompany
    
    if (!targetCompany || !userMenu.selectedUserForMenu) {
      console.error('Missing company or selectedUserForMenu:', { 
        currentCompany, 
        selectedItems, 
        targetCompany, 
        userMenu 
      })
      store.dispatch(
        addNotification({
          message: 'Missing company or user information',
          type: 'error',
          duration: 5000,
        })
      )
      return
    }

    try {
      const result = await CompanyService.removeCompanyUser(targetCompany.id, userMenu.selectedUserForMenu.user_id)

      if (result.success) {
        store.dispatch(
          addNotification({
            message: 'User removed successfully',
            type: 'success',
            duration: 5000,
          })
        )
        handleUserMenuClose()
        loadCompanyUsers(targetCompany.id)
        // Reload companies to update user counts
        if (isPlatformAdmin || isGlobalAdmin) {
          loadCompanies()
        }
      } else {
        console.error('Remove failed:', result.error)
        store.dispatch(
          addNotification({
            message: result.error || 'Failed to remove user',
            type: 'error',
            duration: 5000,
          })
        )
      }
    } catch (error) {
      console.error('Error removing user:', error)
      store.dispatch(
        addNotification({
          message: `Error removing user: ${error instanceof Error ? error.message : 'Unknown error'}`,
          type: 'error',
          duration: 5000,
        })
      )
    } finally {
      handleUserMenuClose()
    }
  }, [currentCompany, selectedItems, userMenu, handleUserMenuClose, loadCompanyUsers, loadCompanies, isPlatformAdmin, isGlobalAdmin])

  const resetEditUserForm = useCallback(() => {
    setEditUserForm({
      role: CompanyUserRole.USER,
      department: '',
      title: '',
      permissions: [],
    })
  }, [])

  const handleEditUser = useCallback(async () => {
    // Selected company before the stale currentCompany prop, as in handleInviteUser.
    const targetCompany = selectedItems.selectedCompany || currentCompany
    
    if (!targetCompany || !selectedItems.selectedUser) {
      console.error('Missing company or selectedUser:', { 
        currentCompany, 
        selectedItems, 
        targetCompany 
      })
      store.dispatch(
        addNotification({
          message: 'Missing company or user information',
          type: 'error',
          duration: 5000,
        })
      )
      return
    }

    try {
      // Only what changed is sent (see userEditChanges); nothing changed means nothing to save.
      const changes = userEditChanges(selectedItems.selectedUser, editUserForm, {
        canAssignRole,
      })
      const result =
        Object.keys(changes).length === 0
          ? { success: true }
          : await CompanyService.updateCompanyUser(
              targetCompany.id,
              selectedItems.selectedUser.user_id,
              changes
            )

      if (result.success) {
        store.dispatch(
          addNotification({
            message: 'User updated successfully',
            type: 'success',
            duration: 5000,
          })
        )
        setDialogs(prev => ({ ...prev, showEditUserDialog: false }))
        setSelectedItems(prev => ({ ...prev, selectedUser: null }))
        resetEditUserForm()
        loadCompanyUsers(targetCompany.id)
      } else {
        console.error('Update failed:', result.error)
        store.dispatch(
          addNotification({
            message: result.error || 'Failed to update user',
            type: 'error',
            duration: 5000,
          })
        )
      }
    } catch (error) {
      console.error('Error updating user:', error)
      store.dispatch(
        addNotification({
          message: `Error updating user: ${error instanceof Error ? error.message : 'Unknown error'}`,
          type: 'error',
          duration: 5000,
        })
      )
    }
  }, [currentCompany, selectedItems, editUserForm, canAssignRole, resetEditUserForm, loadCompanyUsers])

  const handleEditUserOpen = useCallback((user: CompanyUser) => {
    setSelectedItems(prev => ({ ...prev, selectedUser: user }))
    setEditUserForm({
      // A custom role's key, not the legacy `user` cloud stores for it (SC-439).
      role: memberRoleValue(user),
      department: user.department || '',
      title: user.title || '',
      permissions: user.permissions || [],
    })
    setDialogs(prev => ({ ...prev, showEditUserDialog: true }))
    handleUserMenuClose()
  }, [handleUserMenuClose])

  const handleEditCompany = useCallback(async (
    company?: CompanyWithStats,
  ): Promise<{ success: boolean; company?: CompanyWithStats }> => {
    // The opened company comes before the currentCompany prop: the prop is loaded once and goes
    // stale, and in the platform view it is a different company from the one being edited.
    const targetCompany = company || selectedItems.selectedCompany || currentCompany

    if (!targetCompany) {
      console.error('Missing company for edit:', { currentCompany, selectedItems, company })
      store.dispatch(
        addNotification({
          message: 'Missing company information',
          type: 'error',
          duration: 5000,
        })
      )
      return { success: false }
    }

    try {
      console.log('[handleEditCompany] targetCompany.id:', targetCompany.id);
      console.log('[handleEditCompany] companyForm.id:', companyForm.id);
      console.log('[handleEditCompany] companyForm:', JSON.stringify(companyForm, null, 2));
      
      // Use companyForm.id as fallback if targetCompany.id is undefined
      const companyId = targetCompany.id || companyForm.id;
      
      // Validate required data
      if (!companyId) {
        throw new Error('Company ID is required');
      }
      
      if (!companyForm || Object.keys(companyForm).length === 0) {
        throw new Error('Company form data is required');
      }
      
      // The form was filled with the company's default role. Resending it unchanged would
      // overwrite a custom default role key the form can't show, so only a change is sent.
      const roleChecked = withoutUnchangedDefaultRole(
        companyForm,
        defaultRoleAtOpenRef.current ?? companyDefaultRoleValue(targetCompany)
      )
      // Billing keys travel only with a verified platform admin's save: the form fills them with
      // fallbacks, and cloud refuses a company admin's changed value. A change that cannot be
      // sent is said so: dropping it would show "updated" for an edit that was not saved.
      if (!verifiedPlatformAdmin && changedBillingKeys(companyForm, targetCompany).length > 0) {
        store.dispatch(
          addNotification({
            message:
              'Billing changes need a verified platform admin session. Reload and try again.',
            type: 'error',
            duration: 5000,
          })
        )
        return { success: false }
      }
      const settingsToSend = verifiedPlatformAdmin ? roleChecked : withoutBillingKeys(roleChecked)
      const result = isPlatformAdmin || isGlobalAdmin 
        ? await CompanyService.updateCompany(companyId, settingsToSend)
        : await CompanyService.updateCompanySettings(companyId, settingsToSend)

      if (result.success && result.company) {
        store.dispatch(
          addNotification({
            message: 'Company updated successfully',
            type: 'success',
            duration: 5000,
          })
        )
        setDialogs(prev => ({ ...prev, showEditCompanyDialog: false }))
        setSelectedItems(prev => ({ ...prev, selectedCompany: null }))
        resetCompanyForm()

        // Reload companies if platform admin
        if (isPlatformAdmin || isGlobalAdmin) {
          loadCompanies()
        }
        return { success: true, company: result.company as CompanyWithStats }
      }
      store.dispatch(
        addNotification({
          message: result.error || 'Failed to update company',
          type: 'error',
          duration: 5000,
        })
      )
      return { success: false }
    } catch (error) {
      console.error('Error updating company:', error)
      store.dispatch(
        addNotification({
          message: 'Error updating company',
          type: 'error',
          duration: 5000,
        })
      )
      return { success: false }
    }
  }, [
    currentCompany,
    selectedItems,
    companyForm,
    isPlatformAdmin,
    isGlobalAdmin,
    verifiedPlatformAdmin,
    resetCompanyForm,
    loadCompanies,
  ])

  const handleEditCompanyOpen = useCallback((company: CompanyWithStats) => {
    defaultRoleAtOpenRef.current = companyDefaultRoleValue(company)
    setSelectedItems(prev => ({ ...prev, selectedCompany: company }))
    setCompanyForm({
      id: company.id,
      name: company.name,
      domain: company.domain,
      subdomain: company.subdomain || '',
      matrix_chat_domain: company.matrix_chat_domain || '',
      platform_name: company.platform_name || company.name,
      platform_url: company.platform_url || `https://${company.domain}`,
      logo_url: company.logo_url || '',
      primary_color: company.primary_color || '#389acb',
      secondary_color: company.secondary_color || '#ffffff',
      accent_color: company.settings?.accent_color || '',
      link_color: company.settings?.link_color || '',
      surface_tint_color: company.settings?.surface_tint_color || '',
      allowed_domains: company.allowed_domains || [company.domain],
      auto_assignment_enabled: company.auto_assignment_enabled !== undefined ? company.auto_assignment_enabled : true,
      // A custom default role shows as itself, not as Member (SC-439).
      default_role: companyDefaultRoleValue(company),
      ...billingFormValues(company),
      base_price: company.base_price || 0,
      price_per_user: company.price_per_user || 0,
      matrix_chat_enabled: company.matrix_chat_enabled ?? false,
    })
    // Use setTimeout to ensure state is set before opening dialog
    setTimeout(() => {
      setDialogs(prev => ({ ...prev, showEditCompanyDialog: true }))
    }, 0)
  }, [])

  const openDialog = useCallback((dialogName: keyof DialogState) => {
    // Reset form when opening create dialog
    if (dialogName === 'showCreateDialog') {
      resetCompanyForm()
    }
    setDialogs(prev => ({ ...prev, [dialogName]: true }))
  }, [resetCompanyForm])

  const closeDialog = useCallback((dialogName: keyof DialogState) => {
    setDialogs(prev => ({ ...prev, [dialogName]: false }))
  }, [])

  return {
    // State
    userRole,
    userPermissions,
    isCompanyManager,
    companies,
    companyUsers,
    totalCompanyUsers,
    dialogs,
    companyForm,
    inviteForm,
    editUserForm,
    selectedItems,
    userMenu,
    loading: { loading, loadingUsers, loadingUserInfo },
    
    // Actions
    loadCompanies,
    loadCompanyUsers,
    handleCreateCompany,
    handleInviteUser,
    handleUserMenuOpen,
    handleUserMenuClose,
    handleRemoveUser,
    handleEditUser,
    handleEditUserOpen,
    handleEditCompany,
    handleEditCompanyOpen,
    openDialog,
    closeDialog,
    
    // Form setters
    setCompanyForm,
    setInviteForm,
    setEditUserForm,
    setSelectedItems,
    
    // Constants
    availablePermissions: AVAILABLE_PERMISSIONS,
  }
} 